import React from "react";
import Breadcrumb from "@/components/Breadcrumb";
import { getDetailBySlug } from "@/data/service-solution-details";
import RootNotFound from "@/app/not-found";
import { Zap } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { SITE_URL, normalizeLocale, sitePageUrl, ogLocaleTag } from "@/lib/site";

const seoBySlug = {
  "ai-apps-solutions": {
    metaTitle: "AI Apps & Solutions Company | Highlandgroup",
    metaDescription:
      "Highlandgroup is an AI solutions company building custom AI apps and intelligent software for businesses in Switzerland and Europe.",
    metaKeywords:
      "AI solutions company, AI app development company, custom AI software",
    ogTitle: "Custom AI Apps & Solutions",
    ogDescription:
      "Build intelligent software and tailored AI apps with Highlandgroup.",
    serviceType: "Custom AI app development and intelligent software solutions",
  },
  "business-automation-ai": {
    metaTitle: "AI Automation Services for Business | Highlandgroup",
    metaDescription:
      "AI automation services for Swiss and European businesses. Streamline workflows, reduce manual tasks, and improve efficiency with Highlandgroup.",
    metaKeywords:
      "AI automation services, AI for business automation, business automation services Europe",
    ogTitle: "Business Automation & AI Services",
    ogDescription:
      "Automate workflows and improve business efficiency with AI-powered systems.",
    serviceType: "AI automation services and workflow optimization",
  },
  "website-design-development": {
    metaTitle: "Web Development Company Switzerland | Highlandgroup",
    metaDescription:
      "Highlandgroup is a web development company in Switzerland creating modern, high-converting websites for businesses and growth-focused brands.",
    metaKeywords:
      "web development company Switzerland, website development cost Switzerland, website design Switzerland",
    ogTitle: "Website Design & Development",
    ogDescription:
      "Build a modern website that supports trust, growth, and conversions.",
    serviceType: "Website design and development",
  },
  "ecommerce-development": {
    metaTitle: "eCommerce Development Company Switzerland",
    metaDescription:
      "Highlandgroup builds custom eCommerce solutions in Switzerland for brands that need stronger user experience, performance, and sales growth.",
    metaKeywords:
      "eCommerce development company Switzerland, custom eCommerce development, online store development",
    ogTitle: "eCommerce Development Solutions",
    ogDescription:
      "Launch or upgrade an online store built for growth and better conversions.",
    serviceType: "eCommerce development",
  },
  "hotel-digital-solutions": {
    metaTitle: "Hotel Digital Solutions Switzerland | Highlandgroup",
    metaDescription:
      "Hotel digital solutions for Switzerland and Europe, including websites, booking-focused systems, automation, and digital strategy for hospitality.",
    metaKeywords:
      "hotel digital solutions, hotel IT solutions Switzerland, hotel booking system development Switzerland",
    ogTitle: "Hotel Digital Solutions for Hospitality Growth",
    ogDescription:
      "Support direct revenue, guest experience, and efficiency with digital solutions built for hotels.",
    serviceType: "Hotel digital solutions for hospitality businesses",
  },
  "saas-startup-products": {
    metaTitle: "SaaS Development Company for Startups | Highlandgroup",
    metaDescription:
      "Highlandgroup is a SaaS development company building MVPs, startup products, and scalable software for founders in Switzerland and Europe.",
    metaKeywords:
      "SaaS development company, custom SaaS development company Europe, startup product development",
    ogTitle: "SaaS & Startup Product Development",
    ogDescription:
      "Launch and scale SaaS products with a practical development partner.",
    serviceType: "SaaS and startup product development",
  },
  "marketing-tech-consulting": {
    metaTitle: "AI Consulting Services Europe | Highlandgroup",
    metaDescription:
      "Strategic AI consulting services in Europe for automation, digital growth, product planning, and technology decisions that support business results.",
    metaKeywords:
      "AI consulting services Europe, technology consulting services, marketing and tech consulting",
    ogTitle: "Marketing & Tech Consulting",
    ogDescription:
      "Get strategic guidance for AI, automation, software, growth, and product planning.",
    serviceType: "Marketing and technology consulting",
  },
};

export default function ServiceDetailPage({ params }) {
  const { slug } = params;
  const locale = normalizeLocale(params?.locale);
  const item = getDetailBySlug("services", slug);

  if (!item) {
    return <RootNotFound />;
  }

  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Services", href: "/services" },
    { label: item.title },
  ];

  const pageUrl = sitePageUrl(locale, "services", slug);
  const imageUrl =
    item.image?.startsWith("http") ? item.image : `${SITE_URL}${item.image}`;

  const seo = seoBySlug[slug] || {
    metaTitle: `${item.title} | Highlandgroup`,
    metaDescription: item.summary,
    metaKeywords: `${item.title}, Highlandgroup services`,
    ogTitle: item.title,
    ogDescription: item.summary,
    serviceType: item.title,
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Highlandgroup",
        url: `${SITE_URL}/`,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/logo.png`,
        },
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: seo.metaTitle,
        description: seo.metaDescription,
        inLanguage: locale,
        about: {
          "@id": `${SITE_URL}/#organization`,
        },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: imageUrl,
        },
      },
      {
        "@type": "Service",
        "@id": `${pageUrl}#service`,
        name: item.title,
        provider: {
          "@type": "Organization",
          name: "Highlandgroup",
          url: `${SITE_URL}/`,
        },
        url: pageUrl,
        serviceType: seo.serviceType,
        description: seo.metaDescription,
        areaServed: ["Switzerland", "Europe"],
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: sitePageUrl(locale),
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: sitePageUrl(locale, "services"),
          },
          {
            "@type": "ListItem",
            position: 3,
            name: item.title,
            item: pageUrl,
          },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{seo.metaTitle}</title>

        <meta name="description" content={seo.metaDescription} />
        <meta name="keywords" content={seo.metaKeywords} />
        <meta name="robots" content="index, follow" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:title" content={seo.ogTitle} />
        <meta property="og:description" content={seo.ogDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:locale" content={ogLocaleTag(locale)} />
        <meta property="og:site_name" content="Highlandgroup" />
        <meta property="og:image" content={imageUrl} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.ogTitle} />
        <meta name="twitter:description" content={seo.ogDescription} />
        <meta name="twitter:image" content={imageUrl} />

        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-[#F8F9FA] text-charcoal selection:bg-accent-red/10">
        {/* 1. Header Section */}
        <div className="bg-white border-b border-border-light/50">
          <Breadcrumb
            items={breadcrumbItems}
            title={item.title}
            subtitle={item.subtitle}
            description={item.summary}
          />
        </div>

        <section className="py-12 lg:py-20">
          <div className="container mx-auto px-5 sm:px-8 md:px-12">

            {/* 2. Hero Visual with Overlapping Stats */}
            <div className="relative mb-24">
              <div className="relative h-[300px] sm:h-[450px] lg:h-[600px] w-full rounded-[2.5rem] overflow-hidden shadow-2xl shadow-charcoal/10 border border-white">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/20 to-transparent" />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              {/* 3. Main Body Content (Left Side) */}
              <div className="lg:col-span-8">
                <div className="prose prose-lg max-w-none">
                  <div className="space-y-8 text-muted leading-[1.8] text-lg">
                    {item.content.map((paragraph, idx) => (
                      <p key={idx} className={idx === 0 ? "text-charcoal font-medium text-xl" : ""}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>

                {/* 4. Features/What's Included */}
                {item.detailCards && item.detailCards.length > 0 && (
                  <div className="mt-20">
                    <div className="flex items-center gap-4 mb-10">
                      <h2 className="text-3xl font-bold text-charcoal tracking-tight">
                        Service Capabilities
                      </h2>
                      <div className="h-px flex-1 bg-border-light/60" />
                    </div>

                    <div className="grid gap-6 sm:grid-cols-2">
                      {item.detailCards.map((card) => (
                        <div
                          key={card.title}
                          className="group p-8 rounded-[2rem] bg-white border border-border-light/80 hover:border-accent-red/20 hover:shadow-xl hover:shadow-charcoal/5 transition-all duration-500"
                        >
                          <div className="mb-5 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-bg-soft text-accent-red group-hover:bg-accent-red group-hover:text-white transition-all duration-500">
                            <Zap size={24} />
                          </div>
                          <h3 className="text-xl font-bold text-charcoal mb-3">{card.title}</h3>
                          <p className="text-muted leading-relaxed">
                            {card.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}